import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  Background,
  Logo,
  Title,
  InputWrapper,
  InputBox,
  FloatingLabel,
  Button,
  FormLogin,
  ErrorMessage,
  SubTitle,
} from "./styles";
import { userAPI } from "api";

const LoginPage = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isResetPassword, setIsResetPassword] = useState(false);

  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onButton = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (isResetPassword) onChangePassword(e);
    else onLogin(e);
  };

  const onLogin = async (e?: React.FormEvent) => {
    if (errorTimerRef.current) {
      setUsernameError(false);
      setPasswordError(false);
      clearTimeout(errorTimerRef.current);
    }

    if (username === "") {
      setUsernameError(true);
      setErrorMessage("아이디를 적어주세요");

      errorTimerRef.current = setTimeout(() => {
        setUsernameError(false);
      }, 2000);

      return;
    }

    if (password === "") {
      setPasswordError(true);
      setErrorMessage("패스워드를 적어주세요");

      errorTimerRef.current = setTimeout(() => {
        setPasswordError(false);
      }, 2000);

      return;
    }

    try {
      const res = await userAPI.login(username, password);

      sessionStorage.setItem("access_token", res.data.access_token);
      sessionStorage.setItem("token_time", Date.now().toString());
      sessionStorage.setItem("username", username);
      sessionStorage.setItem("ou_admin", res.data.ou_admin);
      sessionStorage.setItem("domain_admin", res.data.domain_admin);
      sessionStorage.setItem("ou", res.data.ou);

      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
    } catch (error: any) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const detailMessage =
        typeof detail === "string" ? detail : detail?.message;

      if (
        status === 422 &&
        (detailMessage === "Incorrect username or password" ||
          detailMessage === "Invalid credentials" ||
          detailMessage === "Bad credentials")
      ) {
        setUsernameError(true);
        setPasswordError(true);

        setErrorMessage("로그인 정보를 확인해주세요");

        errorTimerRef.current = setTimeout(() => {
          setUsernameError(false);
          setPasswordError(false);
        }, 2000);
      } else if (status === 422 && detailMessage === "Reset password") {
        setPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setIsResetPassword(true);
      } else if (status === 422) {
        setUsernameError(true);
        setPasswordError(true);
        setErrorMessage("아이디 또는 비밀번호가 올바르지 않습니다.");

        errorTimerRef.current = setTimeout(() => {
          setUsernameError(false);
          setPasswordError(false);
        }, 2000);
      } else {
        setUsernameError(true);
        setPasswordError(true);
        setErrorMessage("로그인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");

        errorTimerRef.current = setTimeout(() => {
          setUsernameError(false);
          setPasswordError(false);
        }, 2000);
      }
    }
  };

  const onChangePassword = async (e?: React.FormEvent) => {
    if (errorTimerRef.current) {
      setUsernameError(false);
      setPasswordError(false);
      setNewPasswordError(false);
      setConfirmPasswordError(false);
      clearTimeout(errorTimerRef.current);
    }

    if (username === "") {
      setUsernameError(true);
      setErrorMessage("아이디를 적어주세요");

      errorTimerRef.current = setTimeout(() => {
        setUsernameError(false);
      }, 2000);

      return;
    }

    if (password === "") {
      setPasswordError(true);
      setErrorMessage("기존 비밀번호를 적어주세요");

      errorTimerRef.current = setTimeout(() => {
        setPasswordError(false);
      }, 2000);

      return;
    }

    if (newPassword === "") {
      setNewPasswordError(true);
      setErrorMessage("새 비밀번호를 적어주세요");

      errorTimerRef.current = setTimeout(() => {
        setNewPasswordError(false);
      }, 2000);

      return;
    }

    if (newPassword.length < 6) {
      setNewPasswordError(true);
      setConfirmPasswordError(true);
      setErrorMessage("새 비밀번호는 최소 6자 이상이어야 합니다.");

      errorTimerRef.current = setTimeout(() => {
        setNewPasswordError(false);
        setConfirmPasswordError(false);
      }, 2000);

      return;
    }

    if (confirmPasswordError === "") {
      setConfirmPasswordError(true);
      setErrorMessage("비밀번호 확인을 적어주세요");

      errorTimerRef.current = setTimeout(() => {
        setConfirmPasswordError(false);
      }, 2000);

      return;
    }

    if (newPassword !== confirmPassword) {
      setNewPasswordError(true);
      setConfirmPasswordError(true);
      setErrorMessage("비밀번호 확인이 잘못됬습니다.");

      errorTimerRef.current = setTimeout(() => {
        setNewPasswordError(false);
        setConfirmPasswordError(false);
      }, 2000);

      return;
    }

    try {
      const data = await userAPI.passwordFirstChange(
        username,
        password,
        newPassword
      );

      if (data.status === 200) {
        setUsername("");
        setPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setIsResetPassword(false);
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setUsernameError(true);
        setPasswordError(true);
        setNewPasswordError(true);
        setConfirmPasswordError(true);
        setErrorMessage("비밀번호 변경을 실패하였습니다.");

        errorTimerRef.current = setTimeout(() => {
          setUsernameError(false);
          setPasswordError(false);
          setNewPasswordError(false);
          setConfirmPasswordError(false);
        }, 2000);

        return;
      }
    }
  };

  return (
    <Background>
      <FormLogin onSubmit={onButton}>
        <Logo src={"/logo.svg"} alt="logo" />
        {isResetPassword ? (
          <>
            <Title>Change Password</Title>
            <SubTitle>Your password has expired. </SubTitle>
            <SubTitle>Please set a new one</SubTitle>
          </>
        ) : (
          <Title>OVP Cloud Manager</Title>
        )}

        <div style={{ height: 20 }} />
        <InputWrapper className="show">
          <InputBox
            type="text"
            placeholder=" "
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            $hasError={usernameError}
          />
          <FloatingLabel htmlFor="username" $hasError={usernameError}>
            USERNAME
          </FloatingLabel>
        </InputWrapper>

        <InputWrapper className="show">
          <InputBox
            type="password"
            placeholder=" "
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            $hasError={passwordError}
          />
          <FloatingLabel htmlFor="password" $hasError={passwordError}>
            PASSWORD
          </FloatingLabel>
        </InputWrapper>

        {isResetPassword ? (
          <>
            <InputWrapper className={isResetPassword ? "show" : ""}>
              <InputBox
                type="password"
                placeholder=" "
                id="new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                $hasError={newPasswordError}
              />
              <FloatingLabel htmlFor="password" $hasError={newPasswordError}>
                NEW PASSWORD
              </FloatingLabel>
            </InputWrapper>
            <InputWrapper className={isResetPassword ? "show" : ""}>
              <InputBox
                type="password"
                placeholder=" "
                id="confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                $hasError={confirmPasswordError}
              />
              <FloatingLabel
                htmlFor="password"
                $hasError={confirmPasswordError}
              >
                CONFIRM PASSWORD
              </FloatingLabel>
            </InputWrapper>
          </>
        ) : null}

        <Button type="submit">
          {isResetPassword ? "Change Password" : "Login"}
        </Button>

        <ErrorMessage
          $visible={
            usernameError ||
            passwordError ||
            newPasswordError ||
            confirmPasswordError
          }
        >
          {errorMessage}
        </ErrorMessage>
      </FormLogin>
    </Background>
  );
};

export default LoginPage;
